import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  IconButton,
  LinearProgress,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  alpha,
  useTheme,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AssessmentIcon from '@mui/icons-material/Assessment';
import DnsIcon from '@mui/icons-material/Dns';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import HubIcon from '@mui/icons-material/Hub';
import RefreshIcon from '@mui/icons-material/Refresh';
import SpeedIcon from '@mui/icons-material/Speed';
import TimelineIcon from '@mui/icons-material/Timeline';
import { Bar, BarChart, CartesianGrid, Line, LineChart, ResponsiveContainer, Tooltip as ChartTooltip, XAxis, YAxis } from 'recharts';
import { useNavigate } from 'react-router-dom';

import { getNocReports, NocReportsSummary } from '../../services/adminService';
import { useSnackbar } from '../../contexts/SnackbarContext';
import { formatCodecPair } from '../../utils/codecLabels';

type Period = 'daily' | 'weekly' | 'monthly';
type GraphMode = 'bandwidth' | 'inputs' | 'outputs' | 'broken' | 'history';

const cardSx = {
  borderRadius: 0,
  border: '0.5px solid',
  borderColor: 'divider',
  bgcolor: 'background.paper',
  overflow: 'hidden',
} as const;

const labelSx = {
  fontSize: '0.64rem',
  fontWeight: 900,
  textTransform: 'uppercase' as const,
  letterSpacing: '0.08em',
  color: 'text.disabled',
};

const monoSx = {
  fontFamily: "'DM Mono', monospace",
  fontSize: '0.76rem',
  fontWeight: 800,
};

const formatBitrate = (bps?: number) => {
  const value = Number(bps || 0);
  if (value >= 1_000_000) return `${(value / 1_000_000).toFixed(2)} Mbps`;
  if (value >= 1_000) return `${Math.round(value / 1_000)} Kbps`;
  return `${Math.round(value)} bps`;
};

const formatBytes = (bytes?: number) => {
  const value = Number(bytes || 0);
  if (value >= 1_073_741_824) return `${(value / 1_073_741_824).toFixed(2)} GB`;
  if (value >= 1_048_576) return `${(value / 1_048_576).toFixed(2)} MB`;
  if (value >= 1024) return `${(value / 1024).toFixed(1)} KB`;
  return `${Math.round(value)} B`;
};

const formatUptime = (seconds?: number) => {
  const total = Math.max(0, Math.floor(Number(seconds || 0)));
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
};

const sectionHeaderSx = {
  px: 1.5,
  py: 1,
  borderBottom: '0.5px solid',
  borderColor: 'divider',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 1,
} as const;

const ServiceMetric = ({
  label,
  value,
  helper,
  icon,
  color,
}: {
  label: string;
  value: React.ReactNode;
  helper: string;
  icon: React.ReactNode;
  color: string;
}) => (
  <Paper elevation={0} sx={{ ...cardSx, p: 1.35, minHeight: 104 }}>
    <Stack direction="row" spacing={1.2} alignItems="flex-start">
      <Box sx={{ width: 34, height: 34, border: '0.5px solid', borderColor: color, bgcolor: alpha(color, 0.08), display: 'grid', placeItems: 'center', color }}>
        {icon}
      </Box>
      <Box sx={{ minWidth: 0 }}>
        <Typography sx={labelSx}>{label}</Typography>
        <Typography sx={{ fontSize: '1.35rem', fontWeight: 950, lineHeight: 1.1 }}>{value}</Typography>
        <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary', mt: 0.25 }}>{helper}</Typography>
      </Box>
    </Stack>
  </Paper>
);

const DistributionPanel = ({ title, rows, color }: { title: string; rows: Array<{ name: string; value: number }>; color: string }) => {
  const total = rows.reduce((sum, row) => sum + Number(row.value || 0), 0);
  const visibleRows = rows.length ? rows.slice(0, 8) : [{ name: 'No data', value: 0 }];

  return (
    <Paper elevation={0} sx={{ ...cardSx }}>
      <Box sx={sectionHeaderSx}>
        <Typography sx={{ fontSize: '0.84rem', fontWeight: 950 }}>{title}</Typography>
        <Typography sx={monoSx}>{total}</Typography>
      </Box>
      <Stack spacing={1} sx={{ p: 1.25 }}>
        {visibleRows.map((row) => {
          const pct = total > 0 ? Math.min(100, (Number(row.value || 0) / total) * 100) : 0;
          return (
            <Box key={`${title}-${row.name}`}>
              <Stack direction="row" spacing={1} justifyContent="space-between">
                <Typography sx={{ fontSize: '0.75rem', fontWeight: 850 }} noWrap>{row.name}</Typography>
                <Typography sx={monoSx}>{row.value}</Typography>
              </Stack>
              <LinearProgress variant="determinate" value={pct} sx={{ mt: 0.55, height: 3, bgcolor: 'action.hover', '& .MuiLinearProgress-bar': { bgcolor: color } }} />
            </Box>
          );
        })}
      </Stack>
    </Paper>
  );
};

export const NocReportsPage: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { showSnackbar } = useSnackbar();
  const [period, setPeriod] = useState<Period>('daily');
  const [graphMode, setGraphMode] = useState<GraphMode>('bandwidth');
  const [report, setReport] = useState<NocReportsSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [livePoints, setLivePoints] = useState<Array<{ time: string; bandwidth: number; inputs: number; outputs: number; broken: number }>>([]);

  const load = async (quiet = false) => {
    if (!quiet) setLoading(true);
    try {
      const data = await getNocReports(period);
      setReport(data);
      setLivePoints((prev) => {
        const next = [...prev, {
          time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' }),
          bandwidth: Math.round(Number(data.totals.totalBandwidthBps || 0) / 1000),
          inputs: Number(data.totals.liveInputs || 0),
          outputs: Number(data.totals.activeOutputs || 0),
          broken: Number(data.totals.brokenOutputs || 0),
        }];
        return next.slice(-72);
      });
    } catch (e: any) {
      showSnackbar(e.response?.data?.message || 'Failed to load NOC reports', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    const interval = window.setInterval(() => load(true), 10000);
    return () => window.clearInterval(interval);
  }, [period]);

  const chartData = useMemo(() => {
    if (graphMode === 'history') return report?.history || [];
    return livePoints;
  }, [graphMode, livePoints, report?.history]);

  const sourceRows = report?.groups.inputSources || [];
  const outputRows = report?.groups.outputProtocolsByType || [];
  const outputModeRows = report?.groups.outputModesByType || [];
  const platformRows = report?.groups.platformsByType || [];
  const codecRows = report?.groups.codecs || [];
  const inputProtocolRows = report?.groups.inputProtocolsByType || [];
  const brokenOutputs = report?.brokenOutputs || [];
  const riskTone = Number(report?.totals.brokenOutputs || 0) > 0 ? theme.palette.error.main : theme.palette.success.main;
  const boardMetrics = useMemo(() => {
    if (!report) return null;
    const totals = report.totals;
    const activeOutputs = Number(totals.activeOutputs || 0);
    const broken = Number(totals.brokenOutputs || 0);
    const liveInputs = Number(totals.liveInputs || 0);
    const channels = Number(totals.channels || 0);
    const activeChannels = Number(totals.activeChannels ?? channels);
    const userAccounts = Number(totals.userAccounts || 0);
    const activeUsers = Number(totals.activeUsers || 0);
    const restrictedUsers = Number(totals.restrictedUsers || 0);
    const healthyOutputs = Math.max(0, activeOutputs - broken);
    const outputHealthPct = activeOutputs > 0 ? Math.round((healthyOutputs / activeOutputs) * 100) : 100;
    const channelCoveragePct = channels > 0 ? Math.round((activeChannels / channels) * 100) : 0;
    const activeUserPct = userAccounts > 0 ? Math.round((activeUsers / userAccounts) * 100) : 0;
    const liveChannelPct = channels > 0 ? Math.round((liveInputs / channels) * 100) : 0;
    const fanout = liveInputs > 0 ? activeOutputs / liveInputs : 0;
    const traffic = Number(totals.totalBytesIn || 0) + Number(totals.totalBytesOut || 0);
    const codecUnknowns = codecRows.reduce((sum, row) => /unknown/i.test(row.name) ? sum + Number(row.value || 0) : sum, 0);
    const codecTotal = codecRows.reduce((sum, row) => sum + Number(row.value || 0), 0);
    const codecVisibilityPct = codecTotal > 0 ? Math.round(((codecTotal - codecUnknowns) / codecTotal) * 100) : 100;
    const periodCancellations = (report.history || []).reduce((sum, row) => sum + Number(row.cancellations || 0), 0);
    const hlsFamilyOutputs = outputRows.reduce((sum, row) => (
      /^HLS\s+\//i.test(row.name) || /^HLS\+\s+\//i.test(row.name)
        ? sum + Number(row.value || 0)
        : sum
    ), 0);

    return {
      activeOutputs,
      activeUsers,
      activeUserPct,
      broken,
      channelCoveragePct,
      channels,
      codecUnknowns,
      codecVisibilityPct,
      fanout,
      hlsFamilyOutputs,
      liveChannelPct,
      liveInputs,
      outputHealthPct,
      periodCancellations,
      restrictedUsers,
      topInput: inputProtocolRows[0],
      topOutput: outputRows[0],
      traffic,
      userAccounts,
    };
  }, [codecRows, inputProtocolRows, outputRows, report]);

  if (loading && !report) {
    return <Box sx={{ p: 8, display: 'flex', justifyContent: 'center' }}><CircularProgress size={28} /></Box>;
  }

  return (
    <Box sx={{ maxWidth: 1780, mx: 'auto' }}>
      <Paper elevation={0} sx={{ ...cardSx, p: 1.5, mb: 1.5 }}>
        <Stack direction={{ xs: 'column', lg: 'row' }} spacing={1.5} justifyContent="space-between" alignItems={{ xs: 'stretch', lg: 'center' }}>
          <Stack direction="row" spacing={1.1} alignItems="center">
            <IconButton size="small" onClick={() => navigate('/admin/streams')} sx={{ borderRadius: 0, border: '0.5px solid', borderColor: 'divider' }}><ArrowBackIcon sx={{ fontSize: 18 }} /></IconButton>
            <AssessmentIcon sx={{ color: 'text.secondary' }} />
            <Box>
              <Typography sx={{ fontSize: '1.05rem', fontWeight: 950 }}>NOC Reports</Typography>
              <Typography sx={{ fontSize: '0.72rem', color: 'text.secondary' }}>Operational visibility for live inputs, delivery outputs, protocols, and fault queues.</Typography>
            </Box>
          </Stack>
          <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap justifyContent="flex-end">
            {(['daily', 'weekly', 'monthly'] as Period[]).map((item) => (
              <Chip key={item} label={item} onClick={() => setPeriod(item)} color={period === item ? 'primary' : 'default'} variant={period === item ? 'filled' : 'outlined'} sx={{ borderRadius: 0, fontWeight: 900, textTransform: 'capitalize' }} />
            ))}
            <Tooltip title="Refresh reports">
              <span>
                <IconButton size="small" onClick={() => load()} disabled={loading} sx={{ borderRadius: 0, border: '0.5px solid', borderColor: 'divider', width: 34, height: 34 }}>
                  {loading ? <CircularProgress size={16} /> : <RefreshIcon sx={{ fontSize: 18 }} />}
                </IconButton>
              </span>
            </Tooltip>
          </Stack>
        </Stack>
      </Paper>

      {!report ? <Alert severity="warning" sx={{ borderRadius: 0 }}>No NOC report data available.</Alert> : (
        <Stack spacing={1.5}>
          {boardMetrics && (
            <Paper elevation={0} sx={{ ...cardSx, overflow: 'hidden' }}>
              <Box sx={{ ...sectionHeaderSx, bgcolor: alpha(theme.palette.primary.main, 0.04) }}>
                <Box>
                  <Typography sx={{ fontSize: '0.92rem', fontWeight: 950 }}>Strategic Service Metrics</Typography>
                  <Typography sx={{ fontSize: '0.72rem', color: 'text.secondary' }}>
                    Account exposure, channel utilisation, delivery health, and infrastructure pressure.
                  </Typography>
                </Box>
                <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap justifyContent="flex-end">
                  <Chip label={`${boardMetrics.outputHealthPct}% output health`} color={boardMetrics.broken > 0 ? 'error' : 'success'} sx={{ borderRadius: 0, fontWeight: 900 }} />
                  <Chip label={`${boardMetrics.activeUserPct}% active users`} variant="outlined" sx={{ borderRadius: 0, fontWeight: 900 }} />
                  <Chip label={`${boardMetrics.channelCoveragePct}% active channels`} variant="outlined" sx={{ borderRadius: 0, fontWeight: 900 }} />
                </Stack>
              </Box>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))', lg: 'repeat(4, minmax(0, 1fr))' }, gap: 1.25, p: 1.25 }}>
                <ServiceMetric label="User Base" value={`${boardMetrics.activeUsers}/${boardMetrics.userAccounts}`} helper={`${boardMetrics.restrictedUsers} restricted`} icon={<AssessmentIcon fontSize="small" />} color={theme.palette.primary.main} />
                <ServiceMetric label="Channel Coverage" value={`${boardMetrics.channelCoveragePct}%`} helper={`${report.totals.activeChannels || 0}/${boardMetrics.channels} active`} icon={<DnsIcon fontSize="small" />} color={theme.palette.success.main} />
                <ServiceMetric label="Live Penetration" value={`${boardMetrics.liveChannelPct}%`} helper={`${boardMetrics.liveInputs}/${boardMetrics.channels} live now`} icon={<TimelineIcon fontSize="small" />} color={theme.palette.info.main} />
                <ServiceMetric label="Output Health" value={`${boardMetrics.outputHealthPct}%`} helper={`${boardMetrics.activeOutputs - boardMetrics.broken}/${boardMetrics.activeOutputs} healthy`} icon={<ErrorOutlineIcon fontSize="small" />} color={boardMetrics.broken > 0 ? theme.palette.error.main : theme.palette.success.main} />
                <ServiceMetric label="Delivery Fanout" value={boardMetrics.fanout.toFixed(1)} helper="outputs per live input" icon={<HubIcon fontSize="small" />} color={theme.palette.secondary.main} />
                <ServiceMetric label="Bandwidth" value={formatBitrate(report.totals.totalBandwidthBps)} helper={`${formatBytes(boardMetrics.traffic)} traffic sample`} icon={<SpeedIcon fontSize="small" />} color={theme.palette.info.main} />
                <ServiceMetric label="CPU / RAM" value={`${Math.round(Number(report.totals.cpuUsage || 0))}% / ${Math.round(Number(report.totals.ramUsage || 0))}%`} helper="backend host load" icon={<SpeedIcon fontSize="small" />} color={(Number(report.totals.cpuUsage || 0) > 75 || Number(report.totals.ramUsage || 0) > 80) ? theme.palette.warning.main : theme.palette.success.main} />
                <ServiceMetric label="Telemetry Quality" value={`${boardMetrics.codecVisibilityPct}%`} helper={`${boardMetrics.codecUnknowns} unknown codec reads`} icon={<AssessmentIcon fontSize="small" />} color={boardMetrics.codecUnknowns > 0 ? theme.palette.warning.main : theme.palette.success.main} />
                <ServiceMetric label="Scheduled Removals" value={boardMetrics.periodCancellations} helper="capacity/revenue offload" icon={<ErrorOutlineIcon fontSize="small" />} color={boardMetrics.periodCancellations > 0 ? theme.palette.warning.main : theme.palette.text.secondary} />
                <ServiceMetric label="Top Input" value={boardMetrics.topInput?.value || 0} helper={boardMetrics.topInput?.name || 'no input data'} icon={<DnsIcon fontSize="small" />} color={theme.palette.info.main} />
                <ServiceMetric label="Top Output" value={boardMetrics.topOutput?.value || 0} helper={boardMetrics.topOutput?.name || 'no output data'} icon={<HubIcon fontSize="small" />} color={theme.palette.secondary.main} />
                <ServiceMetric label="HLS Family" value={boardMetrics.hlsFamilyOutputs} helper="standard + HLS+" icon={<HubIcon fontSize="small" />} color={theme.palette.warning.main} />
              </Box>
            </Paper>
          )}

          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))', xl: 'repeat(4, minmax(0, 1fr))' }, gap: 1.25 }}>
            <ServiceMetric label="Live Inputs" value={report.totals.liveInputs} helper={`${report.totals.rtmpInputs || 0} RTMP / ${report.totals.srtInputs || 0} SRT / ${report.totals.mediaBoxInputs} MediaBox / ${report.totals.playKasterInputs} PlayKaster`} icon={<DnsIcon fontSize="small" />} color={theme.palette.success.main} />
            <ServiceMetric label="Bandwidth" value={formatBitrate(report.totals.totalBandwidthBps)} helper={`${formatBytes(report.totals.totalBytesIn)} in / ${formatBytes(report.totals.totalBytesOut)} out`} icon={<SpeedIcon fontSize="small" />} color={theme.palette.info.main} />
            <ServiceMetric label="Output Risk" value={report.totals.brokenOutputs} helper={`${report.totals.activeOutputs} active outputs monitored`} icon={<ErrorOutlineIcon fontSize="small" />} color={riskTone} />
            <ServiceMetric label="Longest Input" value={report.longestRunningInput ? formatUptime(report.longestRunningInput.uptime) : '--'} helper={report.longestRunningInput?.channelName || 'no live input'} icon={<TimelineIcon fontSize="small" />} color={theme.palette.secondary.main} />
          </Box>

          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', xl: 'minmax(0, 1.8fr) minmax(360px, 0.9fr)' }, gap: 1.5 }}>
            <Paper elevation={0} sx={{ ...cardSx, p: 1.5 }}>
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} justifyContent="space-between" alignItems={{ xs: 'stretch', md: 'center' }} sx={{ mb: 1.2 }}>
                <Box>
                  <Typography sx={{ fontSize: '0.9rem', fontWeight: 950 }}>Live Operations Trend</Typography>
                  <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>Refreshes every 10 seconds; period controls the historical panel only.</Typography>
                </Box>
                <Stack direction="row" spacing={0.65} flexWrap="wrap" useFlexGap>
                  {[
                    ['bandwidth', 'Bandwidth'],
                    ['inputs', 'Inputs'],
                    ['outputs', 'Outputs'],
                    ['broken', 'Broken'],
                    ['history', 'Historical'],
                  ].map(([key, label]) => (
                    <Chip key={key} label={label} onClick={() => setGraphMode(key as GraphMode)} color={graphMode === key ? 'secondary' : 'default'} variant={graphMode === key ? 'filled' : 'outlined'} sx={{ borderRadius: 0, fontWeight: 900 }} />
                  ))}
                </Stack>
              </Stack>
              <Box sx={{ height: 310, minWidth: 0 }}>
                <ResponsiveContainer width="100%" height="100%" minWidth={1} minHeight={1}>
                  {graphMode === 'history' ? (
                    <BarChart data={chartData} margin={{ top: 10, right: 16, left: -18, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                      <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                      <ChartTooltip />
                      <Bar dataKey="channels" name="New channels" fill={theme.palette.info.main} />
                      <Bar dataKey="outputs" name="New outputs" fill={theme.palette.secondary.main} />
                      <Bar dataKey="cancellations" name="Scheduled removals" fill={theme.palette.error.main} />
                    </BarChart>
                  ) : (
                    <LineChart data={chartData} margin={{ top: 10, right: 16, left: -18, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="time" tick={{ fontSize: 11 }} minTickGap={24} />
                      <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                      <ChartTooltip />
                      {graphMode === 'bandwidth' && <Line type="monotone" dataKey="bandwidth" name="Bandwidth Kbps" stroke={theme.palette.info.main} dot={false} strokeWidth={2} />}
                      {graphMode === 'inputs' && <Line type="monotone" dataKey="inputs" name="Live inputs" stroke={theme.palette.success.main} dot={false} strokeWidth={2} />}
                      {graphMode === 'outputs' && <Line type="monotone" dataKey="outputs" name="Active outputs" stroke={theme.palette.secondary.main} dot={false} strokeWidth={2} />}
                      {graphMode === 'broken' && <Line type="monotone" dataKey="broken" name="Broken outputs" stroke={theme.palette.error.main} dot={false} strokeWidth={2} />}
                    </LineChart>
                  )}
                </ResponsiveContainer>
              </Box>
            </Paper>

            <Paper elevation={0} sx={{ ...cardSx }}>
              <Box sx={sectionHeaderSx}>
                <Stack direction="row" spacing={0.75} alignItems="center">
                  <ErrorOutlineIcon sx={{ fontSize: 18, color: riskTone }} />
                  <Typography sx={{ fontSize: '0.86rem', fontWeight: 950 }}>Output Risk Queue</Typography>
                </Stack>
                <Chip label={`${brokenOutputs.length} open`} size="small" color={brokenOutputs.length ? 'error' : 'success'} sx={{ borderRadius: 0, fontWeight: 900 }} />
              </Box>
              <TableContainer sx={{ maxHeight: 310 }}>
                <Table size="small" stickyHeader>
                  <TableHead><TableRow><TableCell sx={labelSx}>Channel</TableCell><TableCell sx={labelSx}>Output</TableCell><TableCell sx={labelSx}>Reason</TableCell></TableRow></TableHead>
                  <TableBody>
                    {(brokenOutputs.length ? brokenOutputs : [{ channelName: 'No open output risks', type: '--', protocol: '--', reason: 'All active outputs have a live source.' }]).slice(0, 10).map((output, index) => (
                      <TableRow key={output.id || `${output.channelName}-${index}`} hover>
                        <TableCell sx={{ fontSize: '0.76rem', fontWeight: 900 }}>{output.channelName}</TableCell>
                        <TableCell>
                          <Typography sx={{ fontSize: '0.74rem', fontWeight: 850 }}>{output.type}</Typography>
                          <Typography sx={{ ...monoSx, color: 'text.secondary' }}>{output.protocol}</Typography>
                        </TableCell>
                        <TableCell sx={{ fontSize: '0.73rem', color: 'text.secondary' }}>{output.reason}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          </Box>

          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))', xl: 'repeat(6, minmax(0, 1fr))' }, gap: 1.25 }}>
            <DistributionPanel title="Input Sources" rows={sourceRows} color={theme.palette.success.main} />
            <DistributionPanel title="Input Protocols" rows={inputProtocolRows} color={theme.palette.info.main} />
            <DistributionPanel title="Output Modes" rows={outputModeRows} color={theme.palette.primary.main} />
            <DistributionPanel title="Output Protocols" rows={outputRows} color={theme.palette.secondary.main} />
            <DistributionPanel title="Platforms" rows={platformRows} color={theme.palette.warning.main} />
            <DistributionPanel title="Codecs" rows={codecRows} color={theme.palette.error.main} />
          </Box>

          <Paper elevation={0} sx={{ ...cardSx }}>
            <Box sx={sectionHeaderSx}>
              <Stack direction="row" spacing={0.75} alignItems="center">
                <HubIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                <Typography sx={{ fontSize: '0.86rem', fontWeight: 950 }}>Live Input Details</Typography>
              </Stack>
              <Typography sx={monoSx}>{report.liveStreams.length} live</Typography>
            </Box>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: 'action.hover' }}>
                    <TableCell sx={labelSx}>Channel</TableCell>
                    <TableCell sx={labelSx}>Input</TableCell>
                    <TableCell sx={labelSx}>Codecs</TableCell>
                    <TableCell align="right" sx={labelSx}>Uptime</TableCell>
                    <TableCell align="right" sx={labelSx}>Bandwidth</TableCell>
                    <TableCell align="right" sx={labelSx}>Traffic</TableCell>
                    <TableCell align="right" sx={labelSx}>Outputs</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {report.liveStreams.map((stream) => (
                    <TableRow key={`${stream.channelName}-${stream.sourceType}`} hover>
                      <TableCell sx={{ fontSize: '0.78rem', fontWeight: 900 }}>{stream.channelName || 'Unknown channel'}</TableCell>
                      <TableCell><Chip label={stream.sourceLabel || stream.sourceType || 'input'} size="small" variant="outlined" sx={{ borderRadius: 0, fontWeight: 900 }} /></TableCell>
                      <TableCell sx={monoSx}>{formatCodecPair(stream.videoCodec, stream.audioCodec, 'unknown')}</TableCell>
                      <TableCell align="right" sx={monoSx}>{formatUptime(stream.uptime)}</TableCell>
                      <TableCell align="right" sx={monoSx}>{formatBitrate(stream.bitrate)}</TableCell>
                      <TableCell align="right" sx={monoSx}>{formatBytes(stream.bytesIn)} in / {formatBytes(stream.bytesOut)} out</TableCell>
                      <TableCell align="right" sx={monoSx}>{stream.outputs?.active || 0}/{stream.outputs?.total || 0}</TableCell>
                    </TableRow>
                  ))}
                  {!report.liveStreams.length && <TableRow><TableCell colSpan={7}><Alert severity="info" sx={{ borderRadius: 0 }}>No live inputs currently visible to MediaMTX.</Alert></TableCell></TableRow>}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Stack>
      )}
    </Box>
  );
};
